import { useContext, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import moment from "moment";
import { addDaysToDate, parseAsLocalDate } from "../../../utils";
import Fireworks from "../art/Fireworks";
import { HeartDivider } from "../art/Motifs";
import { between, createRng, round } from "../art/rng";
import Reveal from "../Reveal";
import { ScrollerContext } from "../hooks";
import nightView from "../../../images/royal-mahal/night-view.webp";
import nightMask from "../../../images/royal-mahal/night-view-mask.png";

const rng = createRng(71);
const STARS = Array.from({ length: 44 }, () => ({
  left: round(between(rng, 0, 100)),
  top: round(between(rng, 0, 100)),
  size: round(between(rng, 1, 2.6)),
  delay: round(between(rng, 0, 4)),
}));

// The palace alone, cut from the same painting by its silhouette, so the stars and
// fireworks can pass behind it.
const palaceOnly = { WebkitMaskImage: `url(${nightMask})`, maskImage: `url(${nightMask})` };

/**
 * The last page: the lake palace lit up at night (src/images/royal-mahal/night-view.webp),
 * fireworks bursting in the sky behind it, and the closing message above.
 * In preview it uses the same sample date as the hero.
 */
const Closing = ({ eventDate, isPreview }) => {
  const date = isPreview ? new Date(addDaysToDate(16)) : parseAsLocalDate(eventDate);

  // Light the fireworks once the guest is halfway into the page before this one, so
  // bursts are already in the sky when the night scene comes up.
  const ref = useRef(null);
  const scrollerRef = useContext(ScrollerContext);
  const [lit, setLit] = useState(false);
  useEffect(() => {
    const section = ref.current;
    const scroller = scrollerRef.current || window;
    if (!section) return undefined;
    const check = () => {
      const before = (section.previousElementSibling || section).getBoundingClientRect();
      setLit(before.top + before.height / 2 <= window.innerHeight);
    };
    check();
    scroller.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      scroller.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [scrollerRef]);

  return (
    <section ref={ref} className="rm-closing" aria-label="See you at the wedding">
      <img className="rm-painting rm-closing__view" src={nightView} alt="" aria-hidden="true" draggable="false" />
      <div className="rm-closing__stars" aria-hidden="true">
        {STARS.map((s, i) => (
          <span
            key={i}
            className="rm-closing__star"
            style={{ left: `${s.left}%`, top: `${s.top}%`, width: s.size, height: s.size, animationDelay: `${s.delay}s` }}
          />
        ))}
      </div>
      <Fireworks className="rm-closing__fireworks" active={lit} eager lowBursts />
      <img
        className="rm-painting rm-closing__view rm-closing__view--palace"
        src={nightView}
        style={palaceOnly}
        alt=""
        aria-hidden="true"
        draggable="false"
      />
      <Reveal className="rm-closing__message">
        <HeartDivider />
        <p className="rm-closing__title">Looking forward to celebrating with you!</p>
        <p className="rm-kicker">Save the date</p>
        <p className="rm-kicker">{moment(date).format("MMMM DD, YYYY")}</p>
      </Reveal>
    </section>
  );
};

const mapStateToProps = (state) => ({
  eventDate: state.inviteDetails.invite?.eventDate,
  isPreview: state.inviteDetails?.isPreview,
});

export default connect(mapStateToProps)(Closing);
